/**
 * Format a date/time string or Date object to the user's locale
 * @param date - ISO date string, Date object, or timestamp
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted date string in user's locale
 */
export function toUserLocale(
  date: string | Date | number,
  options?: Intl.DateTimeFormatOptions,
): string {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;

  // Default options for a comprehensive date/time display
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    ...options,
  };

  return dateObj.toLocaleString(undefined, defaultOptions);
}

/**
 * Helper function to get ordinal suffix for day numbers
 * @param day - Day number (1-31)
 * @returns Ordinal suffix (st, nd, rd, th)
 */
function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

/**
 * Format a date with ordinal day and full month name
 * Example: "2nd September 2025, 12:00:00 PM"
 * @param date - ISO date string, Date object, or timestamp
 * @returns Formatted date string with ordinal day
 */
export function toUserLocaleWithOrdinal(date: string | Date | number): string {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;

  const day = dateObj.getDate();
  const ordinalSuffix = getOrdinalSuffix(day);

  // Format the month and year
  const monthYear = dateObj.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  // Format the time
  const time = dateObj.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return `${day}${ordinalSuffix} ${monthYear}, ${time}`;
}

/**
 * Format a date to show only the date part in user's locale
 * @param date - ISO date string, Date object, or timestamp
 * @returns Formatted date string (e.g., "Sep 2, 2025")
 */
export function toUserLocaleDateOnly(date: string | Date | number): string {
  return toUserLocale(date, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a date to show only the time part in user's locale
 * @param date - ISO date string, Date object, or timestamp
 * @returns Formatted time string (e.g., "12:00:00 PM")
 */
export function toUserLocaleTimeOnly(date: string | Date | number): string {
  return toUserLocale(date, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Format a date for compact display (good for tables/lists)
 * @param date - ISO date string, Date object, or timestamp
 * @returns Compact formatted date string (e.g., "Sep 2, 12:00 PM")
 */
export function toUserLocaleCompact(date: string | Date | number): string {
  return toUserLocale(date, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format a date for relative display (e.g., "2 minutes ago")
 * @param date - ISO date string, Date object, or timestamp
 * @returns Relative time string
 */
export function toRelativeTime(date: string | Date | number): string {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();

  // Use Intl.RelativeTimeFormat for proper localization
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (Math.abs(diffDays) >= 1) {
    return rtf.format(-diffDays, "day");
  } else if (Math.abs(diffHours) >= 1) {
    return rtf.format(-diffHours, "hour");
  } else if (Math.abs(diffMinutes) >= 1) {
    return rtf.format(-diffMinutes, "minute");
  } else {
    return rtf.format(-diffSeconds, "second");
  }
}

/**
 * Format a date to HH:MM:SS or HH:MM:SS:MS, with a tooltip for the full date/time.
 * @param date - ISO date string, Date object, or timestamp
 * @param showMs - If true, show milliseconds
 * @returns { display: string, tooltip: string }
 */
export function formatTimeWithTooltip(
  date: string | Date | number,
  showMs = false,
): { display: string; tooltip: string } {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;

  // Tooltip: full locale string
  const tooltip = dateObj.toLocaleString();

  // Display: HH:MM:SS or HH:MM:SS:MS
  const pad = (n: number, len = 2) => n.toString().padStart(len, "0");
  const h = pad(dateObj.getHours());
  const m = pad(dateObj.getMinutes());
  const s = pad(dateObj.getSeconds());
  let display = `${h}:${m}:${s}`;
  if (showMs) {
    display += `:${pad(dateObj.getMilliseconds(), 3)}`;
  }

  return { display, tooltip };
}
